/**
 * CTACard — the primary call-to-action card on the property-file page.
 *
 * Per redesign decision v1.2 item 2: a SINGLE primary CTA driven by the
 * `?intent=` query param.
 *  - intent='buy' (default): emerald-700 "Get Buyer's Report — £29" with
 *    the buyer-anchor copy ("Your solicitor's searches £250–£350 come
 *    after offer accepted…"). The Seller's Pack is rendered as a small
 *    text link beneath the card — that link is owned by the parent page,
 *    not this component.
 *  - intent='sell': emerald-700 "Start Seller's Pack — £79" with the
 *    sign-in warning copy from item 8 ("Sign in by email when you're
 *    ready to upload — takes 30 seconds, no password.").
 *
 * The non-primary product link sits alongside this card in the parent;
 * this component only renders the primary card itself.
 *
 * Action shape — pass EITHER:
 *  - `primaryHref` (string): renders an internal Link. Used for the
 *    seller intent which routes through /login or /selling-documents.
 *  - `onPrimaryAction` (async fn): renders a button that calls the fn
 *    on click. Used for the buyer intent so the parent can chain
 *    `createPlanningReport` + `createCheckoutSession` + redirect to
 *    Stripe in-place. `isProcessing` disables the button + flips the
 *    label while in flight.
 */

import { Link } from 'react-router-dom';

export type CtaIntent = 'buy' | 'sell';
export type Tenure = 'freehold' | 'leasehold';

interface CTACardCommonProps {
  intent: CtaIntent;
  /** Whether the primary action is currently in flight (button-only). */
  isProcessing?: boolean;
  /** Optional inline error to render under the button. */
  errorMessage?: string | null;
}

interface CTACardLinkProps extends CTACardCommonProps {
  primaryHref: string;
  onPrimaryAction?: never;
  /** Sell-intent only: current tenure choice surfaced on the card. */
  tenure?: Tenure;
  /** Sell-intent only: tenure radio change handler. Required when `tenure` is passed. */
  onTenureChange?: (tenure: Tenure) => void;
}

interface CTACardButtonProps extends CTACardCommonProps {
  primaryHref?: never;
  onPrimaryAction: () => void | Promise<void>;
  tenure?: never;
  onTenureChange?: never;
}

type CTACardProps = CTACardLinkProps | CTACardButtonProps;

const PRIMARY_BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-lg bg-emerald-700 px-5 py-3 text-base font-semibold text-white shadow-sm hover:bg-emerald-800 active:bg-emerald-900 disabled:opacity-60 disabled:cursor-not-allowed no-underline';

function PrimaryAction(props: CTACardProps & { defaultLabel: string; processingLabel: string }) {
  const { defaultLabel, processingLabel, isProcessing, errorMessage } = props;
  const label = isProcessing ? processingLabel : defaultLabel;

  if ('onPrimaryAction' in props && props.onPrimaryAction) {
    return (
      <>
        <button
          type="button"
          onClick={() => void props.onPrimaryAction()}
          disabled={isProcessing}
          aria-busy={isProcessing || undefined}
          className={PRIMARY_BUTTON_CLASS}
          data-testid="cta-primary"
        >
          {label}
        </button>
        {errorMessage && (
          <p role="alert" className="mt-3 text-sm text-red-700">
            {errorMessage}
          </p>
        )}
      </>
    );
  }

  return (
    <Link
      to={(props as CTACardLinkProps).primaryHref}
      className={PRIMARY_BUTTON_CLASS}
      data-testid="cta-primary"
    >
      {label}
    </Link>
  );
}

export default function CTACard(props: CTACardProps) {
  if (props.intent === 'sell') {
    const tenure: Tenure = props.tenure ?? 'freehold';
    const onTenureChange = props.onTenureChange;
    return (
      <aside
        className="mt-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        data-testid="cta-card"
        data-intent="sell"
      >
        <h3 className="text-lg font-semibold text-slate-950">Seller's Pack — £79</h3>
        <p className="mt-2 text-sm text-slate-700">
          Generates solicitor-ready TA6{tenure === 'leasehold' ? ', TA7, and LPE1' : ''} forms from a guided questionnaire and
          uploaded documents.
        </p>
        <p className="mt-3 text-sm text-slate-700">
          The Law Society's TA6, TA7, and LPE1 forms are the disclosure documents your buyer's solicitor will request. Solicitors typically charge £300&ndash;£500 to prepare them.
        </p>
        <p className="mt-3 text-sm text-slate-700">
          Sign in to save your draft across devices &mdash; takes 30 seconds, no password.
        </p>

        {/* Tenure selector — surfaced on the CTA so the user sees the
            implication (leasehold adds TA7 + LPE1 to the pack) before
            committing. The choice flows to the wizard via ?tenure=… and
            removes the need for a Step-1 tenure radio. */}
        {onTenureChange && (
          <fieldset className="mt-5" data-testid="cta-tenure-fieldset">
            <legend className="text-sm font-semibold text-slate-950">Tenure</legend>
            <p className="mt-1 text-xs text-slate-600">
              Leasehold properties also generate TA7 and LPE1.
            </p>
            <div className="mt-2 flex gap-4">
              <label className="inline-flex items-center text-sm text-slate-800">
                <input
                  type="radio"
                  name="cta-tenure"
                  value="freehold"
                  checked={tenure === 'freehold'}
                  onChange={() => onTenureChange('freehold')}
                  className="mr-2 accent-emerald-700"
                />
                Freehold
              </label>
              <label className="inline-flex items-center text-sm text-slate-800">
                <input
                  type="radio"
                  name="cta-tenure"
                  value="leasehold"
                  checked={tenure === 'leasehold'}
                  onChange={() => onTenureChange('leasehold')}
                  className="mr-2 accent-emerald-700"
                />
                Leasehold
              </label>
            </div>
          </fieldset>
        )}

        <div className="mt-5">
          <PrimaryAction
            {...props}
            defaultLabel="Start Seller's Pack — £79"
            processingLabel="Starting…"
          />
        </div>
      </aside>
    );
  }

  // Default: buy
  return (
    <aside
      className="mt-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
      data-testid="cta-card"
      data-intent="buy"
    >
      <h3 className="text-lg font-semibold text-slate-950">Buyer's Report — £29</h3>
      <p className="mt-2 text-sm text-slate-700">
        Pre-offer due diligence: planning history, flood risk, EPC, tax flags.
      </p>
      <p className="mt-3 text-sm text-slate-700">
        Your solicitor's searches (£250–£350) come after offer accepted. This is what to
        check before you offer.
      </p>
      <p className="mt-3 text-sm text-slate-700">
        Full results for all four sections, plus a downloadable PDF.
      </p>
      <div className="mt-5">
        <PrimaryAction
          {...props}
          defaultLabel="Get Buyer's Report — £29"
          processingLabel="Redirecting to Stripe…"
        />
      </div>
    </aside>
  );
}
